import { Users, Activity, QrCode, Settings, AlertCircle, Scale, CreditCard, Receipt } from 'lucide-react';
import { useState, useEffect, useContext } from 'react';
import { MoreHorizontal } from 'lucide-react'; // For the "More" option
import MembersTab from './Ownerdashboard/MembersTab'; // Import your MembersTab component
import AttendanceTab from './Ownerdashboard/AttendanceTab'; // Import your AttendanceTab component
import PaymentsTab from './Ownerdashboard/PaymentsTab'; // Import PaymentsTab component
import QRCodeContent from './dashboard/QRCodeContent';
import ProfileSettings from './edashboard/ProfileSettings'; // Import your SettingsTab component    
import WeightTrackingTab from './Ownerdashboard/WeightTrackingTab'; // Import your WeightTrackingTab component
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';

const EmployeeDashboard = () => {
  const { user: authUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('Qrcode');
  const [visitedTabs, setVisitedTabs] = useState(new Set(['Qrcode']));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setVisitedTabs(prev => new Set(prev).add(key));
  };
  const [trainerData, setTrainerData] = useState({
    clients: 0,
    sessionsToday: 0,
    upcomingSessions: 0,
    assignedMembers: [],
    attendance: [],
    fitnessRecords: []
  });
  const user = authUser; // Get logged-in trainer
  const [permissions, setPermissions] = useState({
    memberTabEnabled: false,
    paymentTabEnabled: false,
    expensesSubmitEnabled: false
  });

  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [expenseNote, setExpenseNote] = useState('');
  const [submittingExpense, setSubmittingExpense] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    setSubmittingExpense(true);
    setSubmitMessage('');
    setSubmitError('');

    try {
      await api.post(`/api/expense/${user.gymcode}`, {
        category: expenseCategory,
        amount: parseFloat(expenseAmount),
        date: new Date(expenseDate).toISOString(),
        note: expenseNote
      });

      setSubmitMessage('Submitted to gym owner');
      setExpenseCategory('');
      setExpenseAmount('');
      setExpenseNote('');
      setExpenseDate(new Date().toISOString().split('T')[0]);
    } catch (err) {
      console.error(err);
      setSubmitError(err.response?.data?.message || 'Failed to submit expense');
    } finally {
      setSubmittingExpense(false);
    }
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.gymcode) return;
      try {
        const res = await api.get(`/api/gym/trainer/me/permissions?gymCode=${user.gymcode}`);
        setPermissions(res.data);
      } catch (err) {
        console.error('Error fetching permissions:', err);
      }
    };
    fetchPermissions();
  }, [user?.gymcode]);

  // Fetch trainer-specific data
  useEffect(() => {
    const fetchTrainerData = async () => {
      if (!user?.username) return;
      try {
        // Fetch assigned members
        const membersRes = await api.get('/api/trainer/members');
        const members = membersRes.data;
        setTrainerData(prev => ({ ...prev, assignedMembers: members, clients: members.length }));

        // Fetch sessions today
        const sessionsRes = await api.get('/api/trainer/sessions-today');
        const sessions = sessionsRes.data;
        setTrainerData(prev => ({ ...prev, sessionsToday: sessions.count }));

        // Fetch upcoming sessions
        const upcomingRes = await api.get('/api/trainer/upcoming-sessions');
        const upcoming = upcomingRes.data;
        setTrainerData(prev => ({ ...prev, upcomingSessions: upcoming.count }));

        // Fetch attendance
        const attendanceRes = await api.get('/api/trainer/attendance');
        const attendance = attendanceRes.data;
        setTrainerData(prev => ({ ...prev, attendance }));

        // Fetch fitness records for clients
        const fitnessRes = await api.get('/api/trainer/fitness-records');
        const fitness = fitnessRes.data;
        setTrainerData(prev => ({ ...prev, fitnessRecords: fitness }));
      } catch (error) {
        console.error('Error fetching trainer data:', error);
      }
    };
    fetchTrainerData();
  }, [user?.username]);

  // Close menu when pressing Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex h-screen bg-[#101010] text-gray-200">

      {/* Desktop Sidebar */}
      <div className="w-64 bg-[#181818] border-r border-[#2b2b2b] p-4 hidden md:block">
        <div className="flex items-center gap-3 mb-8 p-2">
          <span className="bg-lime-400 rounded-xl p-2">
            <svg width="24" height="24" viewBox="0 0 448 512">
              <path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z" fill="white" />
            </svg>
          </span>
          <span className="text-xl font-bold">FitScan</span>
        </div>

        <nav className="space-y-1">
          {/* <button
            onClick={() => handleTabChange('Overview')}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${
              activeTab === 'Overview' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
            }`}
          >
            <BarChart2 size={20} />
            <span>Overview</span>
          </button> */}


          <button
            onClick={() => handleTabChange('Qrcode')}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${activeTab === 'Qrcode' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
              }`}
          >
            <QrCode size={20} />
            <span>My QR Code</span>
          </button>

          {permissions.memberTabEnabled && (
            <button
              onClick={() => handleTabChange('Members')}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${activeTab === 'Members' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
                }`}
            >
              <Users size={20} />
              <span>Members</span>
            </button>
          )}

          {permissions.paymentTabEnabled && (
            <button
              onClick={() => handleTabChange('Payments')}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${activeTab === 'Payments' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
                }`}
            >
              <CreditCard size={20} />
              <span>Payments</span>
            </button>
          )}

          {permissions.expensesSubmitEnabled && (
            <button
              onClick={() => handleTabChange('SubmitExpense')}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${activeTab === 'SubmitExpense' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
                }`}
            >
              <Receipt size={20} />
              <span>Submit Expense</span>
            </button>
          )}




          {/* <button
            onClick={() => handleTabChange('Members')}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${
              activeTab === 'Members' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
            }`}
          >
            <Users size={20} />
            <span>Members</span>
          </button> */}

          <button
            onClick={() => handleTabChange('Attendance')}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${activeTab === 'Attendance' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
              }`}
          >
            <Activity size={20} />
            <span>Attendance</span>
          </button>

          <button
            onClick={() => handleTabChange('FitnessTracking')}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${activeTab === 'FitnessTracking' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
              }`}
          >
            <Scale size={20} />
            <span>Fitness Tracking</span>
          </button>
          <button
            onClick={() => handleTabChange('Settings')}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${activeTab === 'Settings' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
              }`}
          >
            <Settings size={20} />
            <span>Settings</span>
          </button>


          {/* 
          <button
            onClick={() => handleTabChange('Equipment')}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left ${
              activeTab === 'Equipment' ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
            }`}
          >
            <Shield size={20} />
            <span>Equipment 🔒</span>
          </button> */}



          {/*  */}

          {/* <button
            //onClick={() => logout()}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-left hover:bg-[#222] text-red-400 mt-4"
          >
            <LogOut size={20} />
            <span>Log Out</span>
          </button> */}
        </nav>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#181818] border-b border-[#2b2b2b] flex justify-between items-center p-4 z-10">
        <div className="flex items-center gap-3">
          <span className="bg-lime-400 rounded-xl p-1">
            <svg width="20" height="20" viewBox="0 0 448 512">
              <path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z" fill="white" />
            </svg>
          </span>
          <span className="font-bold">FitScan</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-gray-300 hover:text-white"
        >
          {/* {mobileMenuOpen ? <p size={24}>X</p> : <Menu size={24} />} */}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="absolute top-0 right-0 w-64 h-full bg-[#181818] border-l border-[#2b2b2b] p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="space-y-1 mt-16">

              <div className="flex items-center gap-3 mb-8 p-2">
                <span className="bg-lime-400 rounded-xl p-2">
                  <svg width="24" height="24" viewBox="0 0 448 512">
                    <path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z" fill="white" />
                  </svg>
                </span>
                <span className="text-xl font-bold">FitScan</span>
              </div>
              {[
                { id: 'Qrcode', icon: <QrCode size={20} />, label: "My QR Code" },
                ...(permissions.memberTabEnabled ? [{ id: 'Members', icon: <Users size={20} />, label: "Members" }] : []),
                ...(permissions.paymentTabEnabled ? [{ id: 'Payments', icon: <CreditCard size={20} />, label: "Payments" }] : []),
                ...(permissions.expensesSubmitEnabled ? [{ id: 'SubmitExpense', icon: <Receipt size={20} />, label: "Submit Expense" }] : []),
                { id: 'Attendance', icon: <Activity size={20} />, label: "Attendance" },
                { id: 'FitnessTracking', icon: <Scale size={20} />, label: "Fitness Tracking" },
                { id: 'Settings', icon: <Settings size={20} />, label: "Settings" }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    handleTabChange(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg ${activeTab === item.id ? 'bg-[#222] text-lime-400' : 'hover:bg-[#222]'
                    }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}

              {/* <button
                onClick={() => {
                  //logout();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-[#222] text-red-400 mt-4"
              >
                <LogOut size={20} />
                <span>Log Out</span>
              </button> */}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto pt-16 md:pt-0 pb-24 md:pb-0">
        <div className="p-6">
          {/* Overview Tab */}
          {visitedTabs.has('Overview') && (
            <div className={`space-y-6 ${activeTab === 'Overview' ? '' : 'hidden'}`}>
              <h1 className="text-2xl font-bold">Trainer Overview</h1>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Users size={20} className="text-blue-400" />
                    <h3 className="font-medium">Assigned Clients</h3>
                  </div>
                  <p className="text-3xl font-bold mt-2">{trainerData.clients}</p>
                </div>

                <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Activity size={20} className="text-lime-400" />
                    <h3 className="font-medium">Sessions Today</h3>
                  </div>
                  <p className="text-3xl font-bold mt-2">{trainerData.sessionsToday}</p>

                </div>

                <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle size={20} className="text-amber-400" />
                    <h3 className="font-medium">Upcoming Sessions</h3>
                  </div>
                  <p className="text-3xl font-bold mt-2">{trainerData.upcomingSessions}</p>
                </div>
              </div>

              {/* Trainer-specific chart or list */}
              <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-6">
                <h3 className="font-medium mb-4">Recent Client Activities</h3>
                <div className="space-y-2">
                  {trainerData.assignedMembers.slice(0, 5).map(member => (
                    <div key={member.id} className="flex justify-between">
                      <span>{member.name}</span>
                      <span>Last session: {member.lastSession}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}


          {visitedTabs.has('Qrcode') && (
            <div className={activeTab === 'Qrcode' ? '' : 'hidden'}>

              <QRCodeContent />
            </div>
          )}

          {/* Members Tab */}
          {permissions.memberTabEnabled && visitedTabs.has('Members') && (
            <div className={activeTab === 'Members' ? '' : 'hidden'}>
              <MembersTab />
            </div>
          )}

          {/* Payments Tab */}
          {permissions.paymentTabEnabled && visitedTabs.has('Payments') && (
            <div className={activeTab === 'Payments' ? '' : 'hidden'}>
              <PaymentsTab />
            </div>
          )}

          {/* Submit Expense Tab */}
          {permissions.expensesSubmitEnabled && visitedTabs.has('SubmitExpense') && (
            <div className={activeTab === 'SubmitExpense' ? '' : 'hidden'}>
              <div className="max-w-md mx-auto bg-[#181818] border border-[#2b2b2b] rounded-2xl p-6 shadow-xl mt-6">
                <div className="flex items-center gap-2 mb-6">
                  <Receipt className="w-6 h-6 text-lime-400" />
                  <h2 className="text-xl font-bold text-white">Submit Gym Expense</h2>
                </div>
                
                {submitMessage && (
                  <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-sm text-center font-medium">
                    {submitMessage}
                  </div>
                )}
                {submitError && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm text-center">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleExpenseSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-400 font-semibold mb-1.5">Category</label>
                    <select
                      required
                      value={expenseCategory}
                      onChange={(e) => setExpenseCategory(e.target.value)}
                      className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                    >
                      <option value="" disabled>Select a category</option>
                      <option value="Trainer Salary">Trainer Salary</option>
                      <option value="Equipment Purchase">Equipment Purchase</option>
                      <option value="Equipment Maintenance">Equipment Maintenance</option>
                      <option value="Rent">Rent</option>
                      <option value="Electricity / Utilities">Electricity / Utilities</option>
                      <option value="Supplies">Supplies</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 font-semibold mb-1.5">Amount (₹)</label>
                    <input
                      required
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      value={expenseAmount}
                      onChange={(e) => setExpenseAmount(e.target.value)}
                      className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 font-semibold mb-1.5">Date</label>
                    <input
                      required
                      type="date"
                      value={expenseDate}
                      onChange={(e) => setExpenseDate(e.target.value)}
                      className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 font-semibold mb-1.5">Note (Optional)</label>
                    <textarea
                      rows="3"
                      placeholder="Add details about this expense..."
                      value={expenseNote}
                      onChange={(e) => setExpenseNote(e.target.value)}
                      className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submittingExpense}
                    className="w-full bg-lime-400 hover:bg-lime-300 text-zinc-950 font-bold py-3 rounded-xl transition-all shadow-lg text-sm disabled:opacity-50 mt-2"
                  >
                    {submittingExpense ? 'Submitting...' : 'Submit to Owner'}
                  </button>
                </form>
              </div>
            </div>
          )}


          {/* Attendence Tab */}
          {visitedTabs.has('Attendance') && (
            <div className={activeTab === 'Attendance' ? '' : 'hidden'}>

              <AttendanceTab trainerAttendance={trainerData.attendance} />
            </div>
          )}


          {/* Attendence Tab */}
          {visitedTabs.has('FitnessTracking') && (
            <div className={activeTab === 'FitnessTracking' ? '' : 'hidden'}>
              <WeightTrackingTab clientRecords={trainerData.fitnessRecords} assignedMembers={trainerData.assignedMembers} />
            </div>
          )}

          {/* Setting Tab */}
          {visitedTabs.has('Settings') && (
            <div className={activeTab === 'Settings' ? '' : 'hidden'}>
              <ProfileSettings />
            </div>
          )}


        </div>
      </div>


      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#181818] border-t border-[#2b2b2b] flex justify-around p-2 z-10">
        {[
          // { id: 'Overview', icon: <BarChart2 size={20} />, label: "Overview" },
          { id: 'Qrcode', icon: <QrCode size={20} />, label: "QR Code", key: "qrcode" },
          // { id: 'Members', icon: <Users size={20} />, label: "Members" },

          { id: 'FitnessTracking', icon: <Scale size={20} />, label: "Fitness Tracking" },
          { id: 'Attendance', icon: <Activity size={20} />, label: "Attendance" },


          {
            id: 'more',
            icon: <MoreHorizontal size={20} />,
            label: "More",
            isMenuTrigger: true
          }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => {
              if (item.isMenuTrigger) {
                setMobileMenuOpen(!mobileMenuOpen); // Toggle menu
              } else {
                handleTabChange(item.id);
                setMobileMenuOpen(false);
              }
            }}
            className={`flex flex-col items-center p-2 text-xs ${activeTab === item.id ? 'text-lime-400' : 'text-gray-400'
              } ${mobileMenuOpen && item.isMenuTrigger ? 'text-lime-400' : ''}`}
          >
            {item.icon}
            <span className="mt-1">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default EmployeeDashboard;